import { useEffect, useState } from "react";

import Modal from "@/components/Modal";
import { btn } from "@/components/Page";
import { LeagueBadge } from "@/components/triumvirate/LeagueBadge";
import { LANES, TRIUMVIRATE_MODE } from "@/components/triumvirate/TriumvirateSectionChrome";

function rankLabel(rank, rating) {
  const rankText = rank ? `#${rank}` : "Unranked";
  return `${rankText} · ${Number(rating || 1000).toLocaleString()} rating`;
}

function FighterStrip({ fighter, prefix, showLane = false }) {
  return (
    <div data-testid={`${prefix}-fighter-${fighter.gladiator_id}`} className="border border-zinc-800 bg-zinc-950/45 rounded-sm px-3 py-2">
      {showLane && <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">{fighter.lane} lane</div>}
      <div data-testid={`${prefix}-fighter-name-${fighter.gladiator_id}`} className={`${showLane ? "mt-1" : ""} text-sm text-zinc-200`}>
        {fighter.name}
      </div>
      <div data-testid={`${prefix}-fighter-class-${fighter.gladiator_id}`} className="mt-1 font-cinzel text-base text-amber-200">
        {fighter.cls}
      </div>
    </div>
  );
}

function SidePanel({ title, userName, rank, rating, fighters, prefix, showLanes = false }) {
  return (
    <div data-testid={`${prefix}-panel`} className="border border-amber-900/35 bg-zinc-950/60 rounded-sm p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-cinzel text-xl text-amber-200">{title}</div>
          <div data-testid={`${prefix}-user-name`} className="mt-1 text-sm text-zinc-300">{userName}</div>
          <div data-testid={`${prefix}-rank`} className="mt-1 text-[11px] uppercase tracking-[0.16em] text-zinc-500">
            {rankLabel(rank, rating)}
          </div>
        </div>
        <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId={`${prefix}-badge`} />
      </div>

      <div className="mt-4 grid gap-2">
        {(fighters || []).map((fighter) => (
          <FighterStrip key={fighter.gladiator_id} fighter={fighter} prefix={prefix} showLane={showLanes} />
        ))}
      </div>
    </div>
  );
}

export const TriumvirateTargetingModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  attacker,
  defender,
}) => {
  const [targetMap, setTargetMap] = useState({});

  useEffect(() => {
    if (!open) return;
    const firstDefenderId = defender?.fighters?.[0]?.gladiator_id || "";
    setTargetMap(Object.fromEntries(LANES.map((lane) => [lane, firstDefenderId])));
  }, [open, defender?.user_id]);

  const confirm = () => onConfirm(targetMap);

  if (!open || !attacker || !defender) return null;

  return (
    <Modal open={open} onClose={loading ? undefined : onClose} title="Choose Targets" wide>
      <div data-testid="triumvirate-targeting-modal" className="space-y-5">
        <div className="text-sm text-zinc-400 leading-relaxed">
          Choose which opposing fighter each of your lanes should pressure first. Multiple lanes can focus the same target; enemy lane placements remain hidden.
        </div>

        <section className="grid gap-4 xl:grid-cols-2">
          <SidePanel
            title="Attacker"
            userName={attacker.user_name}
            rank={attacker.rank}
            rating={attacker.rating}
            fighters={attacker.fighters}
            prefix="triumvirate-targeting-attacker"
            showLanes
          />
          <SidePanel
            title="Defender"
            userName={defender.user_name}
            rank={defender.rank}
            rating={defender.rating}
            fighters={defender.fighters}
            prefix="triumvirate-targeting-defender"
          />
        </section>

        <section data-testid="triumvirate-targeting-manual" className="border border-amber-900/35 bg-zinc-950/60 rounded-sm p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Target priorities</div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {LANES.map((lane) => (
              <label key={lane} className="text-sm text-zinc-400">
                <span data-testid={`triumvirate-targeting-label-${lane}`} className="block text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                  {lane} attacks
                </span>
                <select
                  data-testid={`triumvirate-targeting-select-${lane}`}
                  value={targetMap[lane] || defender.fighters?.[0]?.gladiator_id || ""}
                  onChange={(event) => {
                    setTargetMap((current) => ({ ...current, [lane]: event.target.value }));
                  }}
                  className="mt-2 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none"
                >
                  {(defender.fighters || []).map((fighter) => (
                    <option key={fighter.gladiator_id} value={fighter.gladiator_id}>
                      {fighter.name} · {fighter.cls}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap justify-end gap-3">
          <button data-testid="triumvirate-targeting-cancel-button" type="button" onClick={onClose} disabled={loading} className={btn("ghost")}>
            Cancel
          </button>
          <button data-testid="triumvirate-targeting-confirm-button" type="button" onClick={confirm} disabled={loading} className={btn()}>
            {loading ? "Starting Battle…" : "Start Battle"}
          </button>
        </div>
      </div>
    </Modal>
  );
};